import { memo, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import {
  CaseFacilityAddress,
  CaseFacilityAutocomplete,
  FormRow,
  getRenderFacilityAutocompleteItem,
} from '@features/components/CaseFormBlocks/components';
import { Hospital } from '@/api/apis/facilities';
import { useInfiniteHospitalListQuery } from '@/queries/hospital';
import { getCountriesFilterByServiceRegion } from '@/utils/helpers';
import { ServiceRegionId } from '@/constants/serviceRegion.constants';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { getLocationPayload } from '../utils';

type DonorHospitalSelectorProps = {
  disabled?: Partial<Record<keyof CaseFormType, boolean>>;
  hasPermission?: boolean;
  isRequired?: boolean;
};

export const DonorHospitalSelector = memo(
  ({ disabled, hasPermission, isRequired = false }: DonorHospitalSelectorProps) => {
    const { control, setValue } = useFormContext<CaseFormType>();

    const [donorHospitalId, donorHospitalName, serviceRegionId] = useWatch({
      control,
      name: ['donorHospitalId', 'donorHospitalName', 'serviceRegionId'],
    });

    const regionId = serviceRegionId as ServiceRegionId;
    const { countriesByIso2, resolveSubdivisionName } = useCountriesData();

    const handleDonorHospitalSelect = useCallback(
      (item?: Hospital) => {
        if (donorHospitalId === item?.id) return;

        const { location } = item ?? {};

        setValue('donorHospitalName', item?.name ?? '');
        setValue('hospitalCustomLocation', getLocationPayload(location, resolveSubdivisionName), {
          shouldValidate: true,
        });
        setValue('donorHospitalTimeZone', location?.timezone);
      },
      [setValue, donorHospitalId, resolveSubdivisionName],
    );
    const facilityFilters = useMemo(
      () => ({ countries: getCountriesFilterByServiceRegion(regionId, countriesByIso2) }),
      [regionId, countriesByIso2],
    );

    const renderListItem = useCallback(
      (item: object) => getRenderFacilityAutocompleteItem(resolveSubdivisionName)(item as Hospital),
      [resolveSubdivisionName],
    );

    return (
      <FormRow title="Donor Hospital" isRequired={isRequired}>
        <div className="flex-[50%] relative">
          <CaseFacilityAutocomplete
            key={serviceRegionId} // to reset input when region changes
            name="donorHospitalId"
            isDisabled={disabled?.donorHospitalId || !hasPermission}
            control={control}
            placeholder="Select Donor Hospital"
            ariaLabel="Select Donor Hospital"
            infiniteListQuery={useInfiniteHospitalListQuery}
            renderListItem={renderListItem}
            onSelect={handleDonorHospitalSelect}
            defaultInputValue={donorHospitalName}
            filters={facilityFilters}
          />
        </div>
        <CaseFacilityAddress
          disabled={disabled?.donorHospitalId || !hasPermission}
          fieldName="hospitalCustomLocation"
          label="Donor Hospital Address"
        />
      </FormRow>
    );
  },
);
